import { useState } from "react";

export default function ContactSection() {
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError("");
    const form = e.currentTarget;
    const formData = new FormData(form);
    const data = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key !== "_redirect") data.append(key, value.toString());
    });

    try {
      const res = await fetch("https://readdy.ai/api/form/d7r0naij1g8i0g4g50g0", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: data.toString(),
      });
      if (res.ok) {
        setSubmitted(true);
        form.reset();
      } else {
        setError("Submission failed. Please try again.");
      }
    } catch {
      setError("Network error. Please try again.");
    }
  };

  return (
    <section id="contact" className="relative py-16 md:py-24 bg-graphite-950">
      <div className="w-full px-4 sm:px-6 lg:px-8 xl:px-16">
        <div className="max-w-4xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
            <div>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4">
                Let's Talk <span className="text-emerald-accent">Business</span>
              </h2>
              <p className="text-base md:text-lg text-silver-300 leading-relaxed mb-8">
                Whether you're looking for off-the-shelf modules or a bespoke solution, we will build around your operations. Tell us what you need.
              </p>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-graphite-800 flex-shrink-0">
                    <i className="ri-mail-line text-emerald-accent" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-white">Email</p>
                    <p className="text-sm text-silver-400">info@stackmapsystems.com</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-graphite-800 flex-shrink-0">
                    <i className="ri-phone-line text-emerald-accent" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-white">Phone</p>
                    <p className="text-sm text-silver-400">+234 8163 000 0009</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-graphite-800 flex-shrink-0">
                    <i className="ri-map-pin-line text-emerald-accent" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-white">Office</p>
                    <p className="text-sm text-silver-400">15 Kudirat Abiola Way, Oregun, Lagos</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="glass-card p-6 md:p-8">
              {submitted ? (
                <div className="text-center py-10">
                  <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-emerald-accent/10 border border-emerald-accent/20 mb-4">
                    <i className="ri-check-line text-2xl text-emerald-accent" />
                  </div>
                  <h3 className="text-lg font-bold text-white mb-2">Message Sent!</h3>
                  <p className="text-sm text-silver-300">We will get back to you shortly.</p>
                  <button
                    onClick={() => setSubmitted(false)}
                    className="mt-4 text-sm text-emerald-accent hover:text-emerald-hover transition-colors"
                  >
                    Send another message
                  </button>
                </div>
              ) : (
                <form
                  data-readdy-form
                  onSubmit={handleSubmit}
                  className="space-y-4"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-silver-200 mb-1.5">
                        Name
                      </label>
                      <input
                        id="name"
                        name="name"
                        required
                        className="w-full px-4 py-2.5 bg-graphite-800 border border-graphite-700 rounded-lg text-white text-sm placeholder:text-silver-400 focus:outline-none focus:border-emerald-accent/50 transition-colors"
                        placeholder="Your name"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-silver-200 mb-1.5">
                        Email
                      </label>
                      <input
                        id="email"
                        name="email"
                        type="email"
                        required
                        className="w-full px-4 py-2.5 bg-graphite-800 border border-graphite-700 rounded-lg text-white text-sm placeholder:text-silver-400 focus:outline-none focus:border-emerald-accent/50 transition-colors"
                        placeholder="info@stackmapsystems.com"
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="company" className="block text-sm font-medium text-silver-200 mb-1.5">
                      Company
                    </label>
                    <input
                      id="company"
                      name="company"
                      className="w-full px-4 py-2.5 bg-graphite-800 border border-graphite-700 rounded-lg text-white text-sm placeholder:text-silver-400 focus:outline-none focus:border-emerald-accent/50 transition-colors"
                      placeholder="Your company name"
                    />
                  </div>
                  <div>
                    <label htmlFor="interest" className="block text-sm font-medium text-silver-200 mb-1.5">
                      I'm interested in
                    </label>
                    <select
                      id="interest"
                      name="interest"
                      required
                      className="w-full px-4 py-2.5 bg-graphite-800 border border-graphite-700 rounded-lg text-white text-sm focus:outline-none focus:border-emerald-accent/50 transition-colors appearance-none"
                    >
                      <option value="">Select an option</option>
                      <option value="off-the-shelf">Off-the-shelf Products</option>
                      <option value="bespoke">Bespoke Development</option>
                      <option value="demo">Book a Demo</option>
                      <option value="enterprise">Enterprise Consultation</option>
                    </select>
                  </div>
                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-silver-200 mb-1.5">
                      Message
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      rows={4}
                      maxLength={500}
                      required
                      className="w-full px-4 py-2.5 bg-graphite-800 border border-graphite-700 rounded-lg text-white text-sm placeholder:text-silver-400 focus:outline-none focus:border-emerald-accent/50 transition-colors resize-none"
                      placeholder="Tell us about your needs..."
                    />
                    <p className="text-xs text-silver-400 mt-1">Maximum 500 characters</p>
                  </div>
                  {error && (
                    <p className="text-sm text-red-400">{error}</p>
                  )}
                  <button
                    type="submit"
                    className="w-full btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    Send Message
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}